import type {
  ChecklistItem,
  ChecklistNodeLink,
  ChecklistWorkflowStatus,
} from '../types/schema'

// ---------------------------------------------------------------------------
// Risk & Resource matrix ratings
// ---------------------------------------------------------------------------

export type RatingDimensionKey = 'complexity' | 'urgency' | 'importance'

export interface RatingDimension {
  key: RatingDimensionKey
  label: string
  /** Index 0 → star 1 … index 4 → star 5. */
  optionLabels: [string, string, string, string, string]
}

export const RATING_DIMENSIONS: Record<RatingDimensionKey, RatingDimension> = {
  complexity: {
    key: 'complexity',
    label: 'Complexity',
    optionLabels: ['Easy', 'Moderate', 'Hard', 'Difficult', 'Impossible'],
  },
  urgency: {
    key: 'urgency',
    label: 'Urgency',
    optionLabels: ['Later', 'This month', 'This week', 'Today', 'ASAP'],
  },
  importance: {
    key: 'importance',
    label: 'Importance',
    optionLabels: ['Trivial', 'Low', 'Medium', 'High', 'Critical'],
  },
}

export const RATING_DIMENSION_ORDER: RatingDimensionKey[] = ['complexity', 'urgency', 'importance']

/** Clamp any stored rating into the visible 1–5 UI band. */
export const normalizeRatingStars = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(5, Math.max(1, Math.round(value)))
    : 1

export const getRatingOptionLabel = (key: RatingDimensionKey, star: number): string =>
  RATING_DIMENSIONS[key].optionLabels[Math.min(5, Math.max(1, star)) - 1]

/**
 * Backend-only urgency escalation. Raids temporarily push a node's effective
 * urgency to 6 (one above the visible 1–5 band) so raid processes can treat it
 * as crisis-grade without mutating — or showing — the user's real rating. When
 * the raid ends the UI snaps straight back to the stored `urgencyStars`.
 * See AGENTS.md › "Dormant / backend-only checklist node fields".
 */
export const RAID_URGENCY_LEVEL = 6

export const getEffectiveUrgencyLevel = (node: ChecklistItem, isRaidActive: boolean): number =>
  isRaidActive ? RAID_URGENCY_LEVEL : normalizeRatingStars(node.urgencyStars)

// ---------------------------------------------------------------------------
// Header workflow status
// ---------------------------------------------------------------------------

export interface WorkflowStatusOption {
  value: ChecklistWorkflowStatus
  label: string
}

/** Lifecycle order shown in the header dropdown. Default is CREATED. */
export const WORKFLOW_STATUS_OPTIONS: WorkflowStatusOption[] = [
  { value: 'CREATED', label: 'Created' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'ASSESSING', label: 'Assessing' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'ONGOING', label: 'Ongoing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'NOT_REQUIRED', label: 'Not required' },
  { value: 'POSTPONED', label: 'Postponed' },
]

export const DEFAULT_WORKFLOW_STATUS: ChecklistWorkflowStatus = 'CREATED'

export const getWorkflowStatus = (node: ChecklistItem): ChecklistWorkflowStatus => {
  if (node.workflowStatus) return node.workflowStatus
  // Back-compat: nodes completed before the dropdown existed read as Completed.
  return node.isCompleted ? 'COMPLETED' : DEFAULT_WORKFLOW_STATUS
}

export const getWorkflowStatusLabel = (status: ChecklistWorkflowStatus): string =>
  WORKFLOW_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? 'Created'

/**
 * Filter dropdown options for a specific checklist item. ASSESSING is now a
 * derived state — only proof-attached items may inhabit it, and users can't
 * pick it manually. Rules:
 *   - Hide ASSESSING unless the item currently has proof attached OR is
 *     already in ASSESSING (so users can still see the label on the current
 *     item and change back to another state).
 *   - Hide COMPLETED from the manual list when the item is currently
 *     ASSESSING and has a `proofApproverId` — only the approver can take
 *     that edge (enforced separately server-side).
 */
export const filterWorkflowStatusOptions = (
  item: Pick<
    ChecklistItem,
    'proofFileUrl' | 'proofSubmissionId' | 'workflowStatus' | 'proofApproverId'
  >,
  actor?: { uid?: string | null } | null,
): WorkflowStatusOption[] => {
  const hasProof = Boolean(item.proofFileUrl || item.proofSubmissionId)
  const isAssessing = item.workflowStatus === 'ASSESSING'
  const approverOnlyGate = isAssessing && Boolean(item.proofApproverId) && item.proofApproverId !== (actor?.uid ?? null)
  return WORKFLOW_STATUS_OPTIONS.filter((option) => {
    if (option.value === 'ASSESSING' && !hasProof && !isAssessing) return false
    if (option.value === 'COMPLETED' && approverOnlyGate) return false
    return true
  })
}

// ---------------------------------------------------------------------------
// Currencies (20 common, including the Indian rupee)
// ---------------------------------------------------------------------------

export interface CurrencyOption {
  code: string
  symbol: string
  name: string
}

export const CURRENCIES: CurrencyOption[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
]

export const DEFAULT_CURRENCY_CODE = 'USD'

export const getCurrencySymbol = (code: string | undefined | null): string =>
  CURRENCIES.find((currency) => currency.code === code)?.symbol ?? '$'

/**
 * Per-currency scroll-step on the Expense Outlay dial. Hardcoded based on the
 * approximate USD rate at build time and intentionally fixed (the spec calls
 * for it to stay constant forever, not move with live FX). Buckets:
 *   <10 of currency per $1   → step 1
 *   10–50                    → step 5
 *   50–100                   → step 10
 *   100–500                  → step 50
 *   500+                     → step 100
 * Typed input always accepts any integer; the step rule applies to scroll/drag.
 */
export const CURRENCY_SCROLL_STEP: Record<string, number> = {
  USD: 1, EUR: 1, GBP: 1, CNY: 1, AUD: 1, CAD: 1, CHF: 1, SGD: 1,
  HKD: 1, NZD: 1, AED: 1, SAR: 1, BRL: 1,
  SEK: 5, NOK: 5, ZAR: 5,
  INR: 10, RUB: 10,
  JPY: 50,
  KRW: 100,
}

export const getCurrencyScrollStep = (code: string | undefined | null): number =>
  (code && CURRENCY_SCROLL_STEP[code]) || 1

/** Display formatter: thousands-separator commas. Used when the input is
 * NOT focused. While focused, commas are stripped so users can type freely. */
export const formatIntegerWithCommas = (value: number): string =>
  Number.isFinite(value) ? Math.trunc(value).toLocaleString('en-US') : '0'

/** Parse user input — strip commas/whitespace and any non-digit characters. */
export const parseIntegerInput = (raw: string): number => {
  const cleaned = raw.replace(/[^0-9-]/g, '')
  if (!cleaned || cleaned === '-') return 0
  const parsed = Number.parseInt(cleaned, 10)
  return Number.isFinite(parsed) ? parsed : 0
}

// ---------------------------------------------------------------------------
// Date/time lifecycle chip (distinct from the workflow status dropdown)
// ---------------------------------------------------------------------------

export type LifecycleState = 'DONE' | 'BLOCKED' | 'READY' | 'ACTIVE' | 'OVER'

export interface LifecycleChip {
  state: LifecycleState
  label: string
  /** Hover/long-press detail that replaces the old Lock state card. */
  detail: string
}

/** Items with no explicit end stay Active for 3 hours from their start point. */
export const NO_EXPLICIT_END_WINDOW_MS = 3 * 60 * 60 * 1000

const LIFECYCLE_LABEL: Record<LifecycleState, string> = {
  DONE: 'Done',
  BLOCKED: 'Blocked',
  READY: 'Ready',
  ACTIVE: 'Active',
  OVER: 'Over',
}

/** Combine an ISO date ("YYYY-MM-DD") and "HH:mm" into a local epoch ms. */
const combineDateTime = (date: string | null | undefined, time: string | null | undefined): number | null => {
  if (!date) return null
  const safeTime = time && /^\d{2}:\d{2}$/.test(time) ? time : '00:00'
  const parsed = Date.parse(`${date}T${safeTime}:00`)
  return Number.isFinite(parsed) ? parsed : null
}

export interface LifecycleInput {
  isBlocked: boolean
  openBlockerTitles: string[]
  nowMs: number
}

export const getLifecycleChip = (node: ChecklistItem, input: LifecycleInput): LifecycleChip => {
  const status = getWorkflowStatus(node)

  if (status === 'COMPLETED' || node.isCompleted) {
    return { state: 'DONE', label: LIFECYCLE_LABEL.DONE, detail: 'Marked complete.' }
  }

  if (input.isBlocked) {
    const names = input.openBlockerTitles.length > 0 ? input.openBlockerTitles.join(', ') : 'an open dependency'
    return { state: 'BLOCKED', label: LIFECYCLE_LABEL.BLOCKED, detail: `Blocked by ${names}.` }
  }

  const hasTime = node.hasTimeWindow === true
  const startMs = combineDateTime(node.scheduleStartDate, hasTime ? node.scheduleTimeFrom : null)

  // No concrete start → not dated; existing rule → Ready.
  if (startMs === null) {
    return { state: 'READY', label: LIFECYCLE_LABEL.READY, detail: 'No date set. No blocker is holding this node.' }
  }

  let endMs: number | null = null
  if (node.scheduleMode === 'DURATION') {
    endMs = combineDateTime(node.scheduleEndDate, hasTime ? node.scheduleTimeTo : '23:59')
  } else if (hasTime && node.scheduleTimeTo) {
    endMs = combineDateTime(node.scheduleStartDate, node.scheduleTimeTo)
  }
  // No explicit end → 3-hour active window from the start point.
  if (endMs === null || endMs < startMs) endMs = startMs + NO_EXPLICIT_END_WINDOW_MS

  if (input.nowMs < startMs) {
    return { state: 'READY', label: LIFECYCLE_LABEL.READY, detail: `Scheduled to start ${new Date(startMs).toLocaleString()}.` }
  }
  if (input.nowMs > endMs) {
    return { state: 'OVER', label: LIFECYCLE_LABEL.OVER, detail: `Window closed ${new Date(endMs).toLocaleString()}.` }
  }
  return { state: 'ACTIVE', label: LIFECYCLE_LABEL.ACTIVE, detail: `Active until ${new Date(endMs).toLocaleString()}.` }
}

// ---------------------------------------------------------------------------
// Dependency resolution + completion gating
// ---------------------------------------------------------------------------

/**
 * Titles of "is blocked by" links whose target is still incomplete
 * (resolved progress < 100). Targets that can't be resolved are treated as
 * cleared so a deleted element never permanently locks a node.
 */
export const getOpenBlockerTitles = (
  links: ChecklistNodeLink[] | undefined,
  getProgress: (targetId: string) => number | undefined,
): string[] => {
  if (!links || links.length === 0) return []
  return links
    .filter((link) => {
      const progress = getProgress(link.targetId)
      return progress !== undefined && progress < 100
    })
    .map((link) => link.title)
}

export interface CompletionGateInput {
  openBlockerTitles: string[]
}

/**
 * Every reason a node cannot be marked Completed, surfaced together in one
 * toast. The on-site location *verification* (distance check) is performed
 * separately by the caller because it is inherently asynchronous.
 */
export const getCompletionBlockReasons = (node: ChecklistItem, input: CompletionGateInput): string[] => {
  const reasons: string[] = []
  if (node.requiresProofFile && !node.proofFileUrl) {
    reasons.push('Attach the required proof file')
  }
  if (node.requiresLocationPin && !node.targetCoordinates) {
    reasons.push('Set the required location check-in point')
  }
  if (input.openBlockerTitles.length > 0) {
    reasons.push(`Resolve blocking dependencies: ${input.openBlockerTitles.join(', ')}`)
  }
  return reasons
}
